import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { PageTabsHeader } from "../../components";
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogTrigger,
  PageTabs,
  PageTabsList,
  PageTabsTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@tellescope/ui";
import { Spinner } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { TicketsTable } from "../../components/tickets";
import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useMemo, useState } from "react";
import { ListOrdered, Plus } from "lucide-react";
import { TicketQueueCreateEditSheet } from "../../components/dashboard/ticket-queues/TicketQueueSheet";

export const TicketQueuePage = () => {
  const { t } = useTranslation("tickets");
  const navigate = useNavigate();
  const params = useParams();
  const queueId = params.queueId === "default" ? undefined : params.queueId;
  const [isOtherQueuesDialogOpen, setIsOtherQueuesDialogOpen] = useState(false);
  const [isAddQueueSheetOpen, setIsAddQueueSheetOpen] = useState(false);

  const { data: queues } = useTellescopeInfiniteQuery({
    model: "ticket_queues",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const defaultQueueId = useMemo(() => {
    return queues?.[0]?.id;
  }, [queues]);

  const { queuesToDisplay, rolledUpQueues } = useMemo(() => {
    if (!queues) {
      return {
        queuesToDisplay: [],
        rolledUpQueues: [],
      };
    }

    return queues?.reduce<{
      queuesToDisplay: TellescopeModelFromName<"ticket_queues">[];
      rolledUpQueues: TellescopeModelFromName<"ticket_queues">[];
    }>(
      (acc, curr) => {
        if (curr.id === queueId) {
          if (acc.queuesToDisplay.length === 3) {
            return {
              queuesToDisplay: [...acc.queuesToDisplay.slice(0, 2), curr],
              rolledUpQueues: [...acc.rolledUpQueues, ...acc.queuesToDisplay.slice(2)],
            };
          }
          acc.queuesToDisplay.push(curr);
        } else if (acc.queuesToDisplay.length === 3) {
          acc.rolledUpQueues.push(curr);
        } else {
          acc.queuesToDisplay.push(curr);
        }

        return acc;
      },
      {
        queuesToDisplay: [],
        rolledUpQueues: [],
      }
    );
  }, [queueId, queues]);

  if (!queueId && defaultQueueId) {
    return <Navigate replace to={`/tickets/queues/${defaultQueueId}`} />;
  }

  if (!queueId) {
    return (
      <div className="flex flex-col gap-2 items-center justify-center">
        <Spinner />
        <p className="text-muted-foreground">{t("Loading queues...")}</p>
      </div>
    );
  }

  return (
    <>
      <PageTabsHeader>
        <PageTabs value="ticket-queues" className="h-full">
          <PageTabsList>
            <PageTabsTrigger value="views">
              <Link to="/tickets/views">{t("Tickets")}</Link>
            </PageTabsTrigger>
            <PageTabsTrigger value="ticket-queues">{t("Ticket Queues")}</PageTabsTrigger>
          </PageTabsList>
        </PageTabs>
      </PageTabsHeader>
      <div className="p-6">
        <div className="flex gap-2 pb-4">
          <Tabs value={queueId} data-trackingid="tickets-table-view-selector-tabs">
            <TabsList>
              {queuesToDisplay.map((view) => (
                <TabsTrigger key={view.id} value={view.id} asChild>
                  <Link to={`/tickets/queues/${view.id}`}>
                    <ListOrdered className="w-4 h-4" />
                    {view.title}
                  </Link>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {!!rolledUpQueues.length && (
            <Dialog open={isOtherQueuesDialogOpen} onOpenChange={setIsOtherQueuesDialogOpen}>
              <DialogTrigger asChild>
                <button
                  className="p-0 border-0 bg-transparent cursor-pointer text-muted-foreground text-sm"
                  onClick={() => {
                    setIsOtherQueuesDialogOpen(true);
                  }}
                >
                  {t("and {{amount}} more...", { amount: rolledUpQueues.length })}
                </button>
              </DialogTrigger>
              <DialogContent className="p-0 border-0 bg-transparent shadow-none [&>[data-slot=dialog-close]]:top-3 [&>[data-slot=dialog-close]]:right-3 [&>[data-slot=dialog-close]]:focus:ring-0 [&>[data-slot=dialog-close]]:focus:ring-offset-0">
                <Command className="rounded-lg border max-w-lg bg-popover">
                  <CommandInput placeholder="Type a command or search" className="h-12 p-3" />
                  <CommandList>
                    <CommandEmpty>{t("No results found.")}</CommandEmpty>
                    {rolledUpQueues.map((queue) => (
                      <CommandItem key={queue.id} value={queue.id} asChild className="px-4 py-3 cursor-pointer">
                        <Link to={`/tickets/queues/${queue.id}`} onClick={() => setIsOtherQueuesDialogOpen(false)}>
                          <ListOrdered />
                          {queue.title}
                        </Link>
                      </CommandItem>
                    ))}
                  </CommandList>
                </Command>
              </DialogContent>
            </Dialog>
          )}
          <Button variant="ghost" size="icon" hoverLabel={t("Add Queue")} onClick={() => setIsAddQueueSheetOpen(true)}>
            <Plus />
          </Button>
          <TicketQueueCreateEditSheet open={isAddQueueSheetOpen} onOpenChange={setIsAddQueueSheetOpen} />
        </div>

        <TicketsTable
          viewId="default"
          onRowClick={({ id }) => {
            navigate(`/tickets/details/${id}`);
          }}
          queueId={queueId}
          hideViewsTabs
          hideSaveAsView
        />
      </div>
    </>
  );
};
